<template>
	<div class="display-flex flex-wrap scroll-on-overflow">
		<div class="flex-col-12 comments align-self-flex-start pb-2" ref="comments_container">
			<div v-intersect="onIntersect" class="flex-col-12 flex-centerize">
				<AppLoader v-show="loading" />
			</div>
			<IdeaComment
				class="comment"
				v-for="comment in commentsWithoutLast"
				:key="comment.id"
				:comment="comment"
				:ref="`comment_${comment.id}`"
			/>
			<IdeaComment
				class="comment"
				v-intersect="lastCommentIntersect"
				v-if="lastComment"
				:key="lastComment.id"
				:comment="lastComment"
				:ref="`comment_${lastComment.id}`"
			/>
		</div>
		<IdeaCommentPost
			ref="post_comment"
			:ideaId="ideaId"
			class="flex-col-12 align-self-flex-end"
			@comment-added="commentAdded"
			:disabled="loading"
		/>
	</div>
</template>

<script>
import { removeIdeaComment, getIdeaComment, getIdeaComments } from '@/store/modules/inspirationCenter/actions';
import { useComment } from '@/store/modules/inspirationCenter/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import IdeaComment from './IdeaComment.vue';
import IdeaCommentPost from './IdeaCommentPost.vue';
import pusherService, { PUSHER_USAGE_TYPES } from '@/utils/pusher';

export default {
	name: 'IdeaComments',
	props: { ideaId: { type: String } },
	methods: {
		...mapActionsShorthand({ removeIdeaComment, getIdeaComment, getIdeaComments }),
		commentAdded({ commentId }) {
			this.$nextTick(() => {
				this.currentIds = [...new Set([commentId, ...this.currentIds])];
				this.scrollToEnd();
			});
		},
		scrollToEnd() {
			this.$nextTick(() => {
				this.$refs.comments_container.scrollTop = this.$refs.comments_container.scrollHeight; // scroll element to the end
			});
		},
		focusPostCommentInput() {
			this.$refs.post_comment.focus();
		},
		onIntersect(entries, observer, isIntersecting) {
			if (isIntersecting) {
				this.onScrollToTop();
			}
		},
		lastCommentIntersect(entries, observer, isIntersecting) {
			this.isLastCommentIntersecting = isIntersecting;
		},
		async onScrollToTop() {
			if (!this.loading) {
				await this.fetchComments();
			}
		},
		resetPagination(clearIds = true) {
			this.page = 1;
			if (clearIds) {
				this.currentIds = [];
			}
			this.hasNextPage = true;
		},
		async fetchComments() {
			if (this.hasNextPage) {
				this.loading = true;
				try {
					const res = await this.getIdeaComments({
						ideaId: this.ideaId,
						page: this.page,
					});
					this.hasNextPage = Boolean(res.next);
					const isFirstPage = this.page === 1;
					const commentIds = res.results.map(({ id }) => id);
					if (isFirstPage) {
						this.currentIds = commentIds;
					} else {
						const currentScrolledId = this.comments[0].id;
						this.currentIds = [...new Set([...this.currentIds, ...commentIds])];
						this.$nextTick(() => {
							this.$refs[`comment_${currentScrolledId}`][0].$el.scrollIntoView(true);
						});
					}
					this.page++;
				} catch {
					this.$toast.error('Something went wrong, please try again later');
				} finally {
					this.loading = false;
				}
			}
		},
		createIdeaPusherConnection() {
			pusherService.subscribeToIdea(this.ideaId);
			pusherService.listenToEvents([
				pusherService.createPusherEventObj(
					'comment-created',
					async ({ comment_id: commentId }) => {
						await this.getIdeaComment({ ideaId: this.ideaId, commentId });
						this.$nextTick(() => {
							this.currentIds = [...new Set([commentId, ...this.currentIds])];
							if (this.isLastCommentIntersecting) {
								this.scrollToEnd();
								// scroll to end after new comment is added
							}
						});
					},
					PUSHER_USAGE_TYPES.IDEA
				),
				pusherService.createPusherEventObj(
					'comment-updated',
					({ comment_id: commentId }) => this.getIdeaComment({ ideaId: this.ideaId, commentId }),
					PUSHER_USAGE_TYPES.IDEA
				),
				pusherService.createPusherEventObj(
					'comment-removed',
					async ({ comment_id: commentId }) => {
						this.currentIds = this.currentIds.filter((id) => id !== commentId);
						await this.removeIdeaComment({ ideaId: this.ideaId, commentId, skipRequest: true });
					},
					PUSHER_USAGE_TYPES.IDEA
				),
			]);
		},
		removeIdeaPusherConnection() {
			pusherService.clearIdeaChannel(false);
		},
	},
	computed: {
		...mapGettersShorthand({ useComment }),
		comments() {
			return [...this.currentIds]
				.reverse()
				.map(this.useComment)
				.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
		},
		commentsWithoutLast() {
			return this.comments.slice(0, -1);
		},
		lastComment() {
			return this.comments.at(-1);
		},
	},
	async mounted() {
		await this.fetchComments();
		this.scrollToEnd();
		this.createIdeaPusherConnection();
	},
	beforeDestroy() {
		this.removeIdeaPusherConnection();
	},
	components: { IdeaComment, IdeaCommentPost },
	data() {
		return {
			loading: false,
			page: 1,
			hasNextPage: true,
			currentIds: new Set(),
			isLastCommentIntersecting: false,
		};
	},
};
</script>

<style lang="scss" scoped>
$distance-between-comments: 22px;
.comments {
	max-height: calc(100% - 78px);
	overflow: auto;
	.comment:not(:nth-child(2)) {
		margin-top: $distance-between-comments;
	}
}
</style>
